import coordtransform from '@/WGS84.js'
import { tiandituReverseGeocode } from '@/utils/tiandituGeocoder'
import { resolveHeadingDegrees, formatDirectionText, markerIconRotateDeg } from '@/utils/vehicleHeading'

export { formatDirectionText }
import markerIconLv from '@/assets/lv.png'
import markerIconRed from '@/assets/red.png'
import markerIconHui from '@/assets/hui.png'

/* global AMap */

export const MARKER_UTC_STALE_MS = 15 * 60 * 1000

function pad2(n) {
  return String(n).padStart(2, '0')
}

export function lonLatToGcj02(lon, lat) {
  const nlon = Number(lon)
  const nlat = Number(lat)
  if (!Number.isFinite(nlon) || !Number.isFinite(nlat)) return [116.404, 39.915]
  if (Math.max(Math.abs(nlon), Math.abs(nlat)) > 10000) {
    return coordtransform.wgs84togcj02(nlon / 600000.0, nlat / 600000.0)
  }
  return [nlon, nlat]
}

export function utcToGtmFromApi(utc) {
  if (utc == null || utc === '') return ''
  let ms = Number(utc)
  if (!Number.isFinite(ms)) return ''
  if (ms < 1e12) ms *= 1000
  const d = new Date(ms)
  if (Number.isNaN(d.getTime())) return ''
  return `${d.getFullYear()}${pad2(d.getMonth() + 1)}${pad2(d.getDate())}/${pad2(d.getHours())}${pad2(d.getMinutes())}${pad2(d.getSeconds())}`
}

function formatGtm(gtm) {
  if (!gtm || String(gtm).length < 15) return '—'
  try {
    const datePart = gtm.substring(0, 8)
    const timePart = gtm.substring(9)
    return `${datePart.substring(0, 4)}-${datePart.substring(4, 6)}-${datePart.substring(6, 8)} ${timePart.substring(0, 2)}:${timePart.substring(2, 4)}:${timePart.substring(4, 6)}`
  } catch (e) {
    return '—'
  }
}

export function parseSpd(item) {
  if (!item) return 0
  const raw = item.raw && typeof item.raw === 'object' ? item.raw : {}
  const val =
    item.spd ??
    item.gpsSpeed ??
    raw.spd ??
    raw.gpsSpeed ??
    item.speed ??
    item.SPD ??
    raw.speed ??
    0
  const n = typeof val === 'number' ? val : parseFloat(String(val).replace(/,/g, ''))
  return Number.isFinite(n) ? n : 0
}

function formatGarageDurationMinutesSeconds(totalSeconds) {
  if (totalSeconds == null || !Number.isFinite(totalSeconds) || totalSeconds < 0) return ''
  const sec = Math.floor(totalSeconds)
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return `${m}分${s}秒`
}

/** 从定位行解析行/停持续时长（秒），兼容多种后端字段名 */
function resolveGarageStatusDurationSeconds(row) {
  if (!row) return null
  const raw = row.raw && typeof row.raw === 'object' ? row.raw : row
  const sts = resolveBicycleDisplayState(row)
  const spd = parseSpd(row)
  const isMoving = sts === '在线' || (sts !== '离线' && spd > 0)
  const keys = isMoving
    ? ['runTime', 'runDuration', 'runningTime', 'stateTime', 'statusTime', 'duration']
    : ['stopTime', 'stopDuration', 'stoppedTime', 'offlineTime', 'stateTime', 'statusTime', 'duration']
  for (const k of keys) {
    const v = raw[k]
    if (v == null || v === '') continue
    let n = Number(v)
    if (!Number.isFinite(n)) continue
    if (k === 'offlineTime') n *= 60
    if (n > 86400000) n = Math.floor(n / 1000)
    return n
  }
  return null
}

/** 从地址或行政区字段提取区县/市名（车库树后缀「区」） */
export function extractGarageDistrictFromRow(row) {
  if (!row) return ''
  const raw = row.raw && typeof row.raw === 'object' ? row.raw : {}
  const districtCand = [raw.county, raw.district, raw.country, row.country]
  for (const c of districtCand) {
    if (c != null && String(c).trim() !== '' && !/^\d+$/.test(String(c).trim())) {
      return String(c).trim()
    }
  }
  const adr = row.adr || row.location || raw.address || raw.adr || ''
  if (adr) {
    const s = String(adr)
    const m = s.match(/(?:省|自治区|市)([^,，、\s]{2,10}[区县市])/)
    if (m && m[1]) return m[1]
    const m2 = s.match(/([^,，、\s]{2,10}[区县市])/)
    if (m2 && m2[1]) return m2[1]
  }
  const city = row.city || raw.city
  return city != null && String(city).trim() !== '' ? String(city).trim() : ''
}

/** 车库树车辆后缀，如 [48km/h 行8分14秒 郫都区] */
export function formatGarageVehicleTreeSuffix(row) {
  if (!row) return ''
  const spd = parseSpd(row)
  const sts = resolveBicycleDisplayState(row)
  const spdText = `${spd.toFixed(spd % 1 === 0 ? 0 : 1)}km/h`
  const durSec = resolveGarageStatusDurationSeconds(row)
  let action = '停'
  if (sts === '在线' || spd > 0) action = '行'
  const durText = durSec != null ? `${action}${formatGarageDurationMinutesSeconds(durSec)}` : ''
  const district = extractGarageDistrictFromRow(row)
  const parts = [spdText]
  if (durText) parts.push(durText)
  if (district) parts.push(district)
  return `[${parts.join(' ')}]`
}

export function utcMsFromLocationRow(row) {
  if (!row) return null
  const raw = row.raw || {}
  const u = raw.utc != null && raw.utc !== '' ? raw.utc : row.utc
  if (u == null || u === '') return null
  let ms = Number(u)
  if (!Number.isFinite(ms)) return null
  if (ms < 1e12) ms *= 1000
  return ms
}

/** 状态：>15 分钟离线；<=15 分钟且速度 0 停止；否则在线 */
export function getBicycleMarkerState(row) {
  if (!row) return '离线'
  const spd = parseSpd(row)
  const utcMs = utcMsFromLocationRow(row)
  if (utcMs == null || Date.now() - utcMs > MARKER_UTC_STALE_MS) return '离线'
  if (spd <= 0) return '停止'
  return '在线'
}

export function resolveBicycleDisplayState(row) {
  if (!row) return '离线'
  const sts = row.sts != null && String(row.sts).trim() !== '' ? String(row.sts).trim() : ''
  if (sts === '在线' || sts === '停止' || sts === '离线') return sts
  return getBicycleMarkerState(row)
}

export function formatBicycleTableStatus(row) {
  return resolveBicycleDisplayState(row)
}

/** 在线 / 停止 / 离线 → UI 类型 */
export function bicycleStatusTypeFromSts(sts) {
  if (sts === '在线') return 'online'
  if (sts === '停止') return 'stop'
  return 'offline'
}

export function bicycleStatusType(row) {
  return bicycleStatusTypeFromSts(resolveBicycleDisplayState(row))
}

export function bicycleStatusCssClass(stsOrRow) {
  const sts =
    typeof stsOrRow === 'string' ? stsOrRow : resolveBicycleDisplayState(stsOrRow)
  return `is-${bicycleStatusTypeFromSts(sts)}`
}

export function pickBicycleMarkerIconUrl(row) {
  const s = resolveBicycleDisplayState(row)
  if (s === '离线') return markerIconHui
  if (s === '停止') return markerIconRed
  return markerIconLv
}

/** 自定义车标 DOM：仅 img 旋转车头，车牌/速度标签保持水平 */
export function buildBicycleMarkerContent(row, iconUrl) {
  const rot = markerIconRotateDeg(resolveHeadingDegrees(row))
  const plate = String(row.vclN || row.vno || '').trim() || '—'
  const spd = parseSpd(row)
  const spdStr = `${spd.toFixed(spd % 1 === 0 ? 0 : 1)} km/h`
  const root = document.createElement('div')
  root.style.cssText =
    'position:relative;width:0;height:0;pointer-events:none;user-select:none;'
  const img = document.createElement('img')
  img.src = iconUrl
  img.width = 32
  img.height = 32
  img.alt = ''
  img.draggable = false
  img.style.cssText = `position:absolute;left:-16px;top:-16px;width:32px;height:32px;transform:rotate(${rot}deg);transform-origin:50% 50%;pointer-events:auto;cursor:pointer;`
  root.appendChild(img)
  const labelBox = document.createElement('div')
  labelBox.style.cssText =
    'position:absolute;left:50%;top:20px;transform:translateX(-50%);display:flex;flex-direction:column;align-items:center;gap:2px;max-width:7.5rem;padding:4px 8px;border-radius:4px;background:rgba(255,255,255,0.92);border:1px solid rgba(0,0,0,0.08);box-shadow:0 1px 3px rgba(0,0,0,0.12);pointer-events:none;text-align:center;'
  const plateEl = document.createElement('div')
  plateEl.textContent = plate
  plateEl.style.cssText =
    'font-size:12px;font-weight:600;line-height:1.25;color:#303133;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:100%;'
  labelBox.appendChild(plateEl)
  const spdEl = document.createElement('div')
  spdEl.textContent = spdStr
  spdEl.style.cssText =
    'font-size:11px;font-weight:500;line-height:1.2;color:#606266;white-space:nowrap;'
  labelBox.appendChild(spdEl)
  root.appendChild(labelBox)
  return { root, plate, spdStr }
}

function formatIwUpdateTime(row) {
  const g = row && row.gtm ? formatGtm(row.gtm) : '—'
  if (g && g !== '—') return g
  const ms = utcMsFromLocationRow(row)
  if (ms == null) return '—'
  const d = new Date(ms)
  if (Number.isNaN(d.getTime())) return '—'
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`
}

function formatIwMileageKm(row) {
  const raw =
    row && row.mil != null && row.mil !== ''
      ? row.mil
      : row && row.raw && row.raw.mil != null && row.raw.mil !== ''
        ? row.raw.mil
        : null
  const n = raw != null ? Number(raw) : NaN
  return Number.isFinite(n) ? `${n.toFixed(2)} km` : '—'
}

export function unwrapTruckLastLocationRes(res) {
  let data = res && res.data != null ? res.data : null
  if (data && typeof data === 'object' && data.data != null) {
    data = data.data
  }
  return data
}

export function normalizeGetTruckLastLocationData(data) {
  if (!data || typeof data !== 'object') return data
  const out = { ...data }
  if (out.firstVcl == null && out.firstVol != null) {
    out.firstVcl = out.firstVol
  }
  return out
}

export function parseVnosSegments(vnosStr) {
  const out = []
  for (const seg of String(vnosStr || '').split(',')) {
    const s = seg.trim()
    if (!s) continue
    const last = s.lastIndexOf('_')
    if (last <= 0) {
      out.push({ vclN: s, vco: '2' })
    } else {
      out.push({ vclN: s.slice(0, last), vco: s.slice(last + 1) || '2' })
    }
  }
  return out
}

export function buildMultiVnosFormData(vnosStr) {
  const fd = new FormData()
  fd.append('vnos', String(vnosStr || '').trim())
  return fd
}

export function vehiclesToVnos(vehicles) {
  const list = Array.isArray(vehicles) ? vehicles : []
  const parts = []
  const seen = new Set()
  list.forEach((v) => {
    const plate = v && v.vehicleNo != null ? String(v.vehicleNo).trim() : ''
    if (!plate || seen.has(plate)) return
    seen.add(plate)
    const vco = Number(v.plateColor || v.vco || v.vehiclePlateColor || 2)
    parts.push(`${plate}_${vco}`)
  })
  return parts.join(',')
}

export function normalizeLastLocationRow(apiV, fallbackVco) {
  const v = apiV && typeof apiV === 'object' ? apiV : {}
  const gtm = utcToGtmFromApi(v.utc)
  let sts =
    v.sts != null && v.sts !== ''
      ? String(v.sts)
      : getBicycleMarkerState(v)
  return {
    lon: v.lon,
    lat: v.lat,
    vclN: v.vno || v.vclN || '—',
    vco:
      v.vco != null && v.vco !== ''
        ? String(v.vco)
        : String(fallbackVco != null && fallbackVco !== '' ? fallbackVco : '2'),
    adr: v.adr,
    location: v.adr,
    gtm: gtm || undefined,
    spd: v.spd,
    drc:
      v.drc != null && v.drc !== ''
        ? v.drc
        : v.head != null && v.head !== ''
          ? v.head
          : undefined,
    head: v.head,
    agl: v.agl != null && v.agl !== '' ? v.agl : v.head,
    mil: v.mil,
    province: v.province,
    city: v.city,
    status: v.status,
    online: sts === '在线',
    sts,
    raw: { ...v }
  }
}

export function mergeLastLocationItems(data, fallbackVcos) {
  if (!data || typeof data !== 'object') return []
  const vehicles = []
  if (data.firstVcl) vehicles.push(data.firstVcl)
  if (Array.isArray(data.others)) vehicles.push(...data.others.filter(Boolean))
  return vehicles
    .map((v, i) => {
      const fb =
        Array.isArray(fallbackVcos) && fallbackVcos[i] != null && fallbackVcos[i] !== ''
          ? fallbackVcos[i]
          : '2'
      return normalizeLastLocationRow(v, fb)
    })
    .filter((r) => r.lon != null && r.lat != null)
}

export function openVehicleInfoWindow(map, ctx, lng, lat, row, opts) {
  if (!map || typeof AMap === 'undefined') return null
  const options = opts && typeof opts === 'object' ? opts : {}
  const aboveMarker = options.aboveMarker !== false
  if (ctx.activeInfoWindow) {
    try {
      ctx.activeInfoWindow.close()
    } catch (e) {
      /* ignore */
    }
    ctx.activeInfoWindow = null
  }
  const plate = row.vclN || row.vno || '—'
  const spd = parseSpd(row)
  const dir = formatDirectionText(row)
  const timeStr = formatIwUpdateTime(row)
  const statusText =
    options.statusText != null && String(options.statusText).trim() !== ''
      ? String(options.statusText).trim()
      : formatBicycleTableStatus(row)
  const mileageText = formatIwMileageKm(row)
  const lngStr = Number.isFinite(Number(lng)) ? Number(lng).toFixed(6) : '—'
  const latStr = Number.isFinite(Number(lat)) ? Number(lat).toFixed(6) : '—'
  const spdStr = `${spd.toFixed(spd % 1 === 0 ? 0 : 1)}km/h`
  const addrPlaceholder = row.adr || row.location || '解析中...'
  const labelColor = '#606266'
  const valueColor = '#303133'
  const statusColor =
    options.statusColor != null && String(options.statusColor).trim() !== ''
      ? String(options.statusColor).trim()
      : statusText === '离线'
        ? '#909399'
        : statusText === '停止'
          ? '#f56c6c'
          : statusText === '在线'
            ? '#52c41a'
            : '#909399'

  const root = document.createElement('div')
  // 阴影加在卡片上，避免 filter 撑高内容盒导致 tip 锚点与车标错位
  root.style.cssText =
    'display:flex;flex-direction:column;align-items:center;'
  const card = document.createElement('div')
  card.style.cssText =
    'position:relative;width:300px;box-sizing:border-box;padding:12px 16px 12px;background:#fff;border-radius:10px;font-size:13px;color:#303133;line-height:1.55;box-shadow:0 2px 8px rgba(0,0,0,.12)'

  const closeBtn = document.createElement('span')
  closeBtn.setAttribute('role', 'button')
  closeBtn.setAttribute('aria-label', '关闭')
  closeBtn.textContent = '×'
  closeBtn.style.cssText =
    'position:absolute;right:10px;top:10px;z-index:1;cursor:pointer;font-size:20px;line-height:1;color:#c0c4cc;user-select:none'
  closeBtn.addEventListener('click', (e) => {
    e.stopPropagation()
    if (ctx.activeInfoWindow) {
      try {
        ctx.activeInfoWindow.close()
      } catch (err) {
        /* ignore */
      }
      ctx.activeInfoWindow = null
    }
  })
  card.appendChild(closeBtn)

  const header = document.createElement('div')
  header.style.cssText = 'padding:2px 28px 0 0'
  const topRow = document.createElement('div')
  topRow.style.cssText =
    'display:flex;justify-content:space-between;align-items:flex-start;gap:8px'
  const leftTitle = document.createElement('div')
  leftTitle.style.cssText = 'flex:1;min-width:0;word-break:break-all'
  const plateSpan = document.createElement('span')
  plateSpan.style.cssText = 'font-size:16px;font-weight:700;color:#303133'
  plateSpan.textContent = plate
  leftTitle.appendChild(plateSpan)
  const statusEl = document.createElement('span')
  statusEl.style.cssText = `flex-shrink:0;font-size:14px;font-weight:600;color:${statusColor};white-space:nowrap;padding-top:2px`
  statusEl.textContent =
    statusText === '离线' || statusText === '停止' || statusText === '在线'
      ? statusText
      : '—'
  topRow.appendChild(leftTitle)
  topRow.appendChild(statusEl)
  header.appendChild(topRow)
  card.appendChild(header)
  const hr1 = document.createElement('div')
  hr1.style.cssText = 'border:0;border-top:1px solid #ebeef5;margin:0'
  card.appendChild(hr1)

  const body = document.createElement('div')
  body.style.cssText = 'font-size:12px;color:#606266;padding:10px 0 0'
  const addLine = (labelText, valueText) => {
    const line = document.createElement('div')
    const lab = document.createElement('span')
    lab.style.color = labelColor
    lab.textContent = labelText
    const val = document.createElement('span')
    val.style.color = valueColor
    val.textContent = valueText
    line.appendChild(lab)
    line.appendChild(val)
    body.appendChild(line)
  }
  addLine('速度：', spdStr)
  if (!options.hideDirection) {
    addLine('方向：', dir)
  }
  addLine('累计里程：', mileageText)

  const llRow = document.createElement('div')
  llRow.style.cssText = 'font-size:12px;line-height:1.5;word-break:break-all'
  const lonLab = document.createElement('span')
  lonLab.style.color = labelColor
  lonLab.textContent = '经度：'
  const lonVal = document.createElement('span')
  lonVal.style.color = valueColor
  lonVal.textContent = lngStr
  const midSp = document.createElement('span')
  midSp.textContent = '\u00A0\u00A0\u00A0\u00A0'
  const latLab = document.createElement('span')
  latLab.style.color = labelColor
  latLab.textContent = '纬度：'
  const latVal = document.createElement('span')
  latVal.style.color = valueColor
  latVal.textContent = latStr
  llRow.appendChild(lonLab)
  llRow.appendChild(lonVal)
  llRow.appendChild(midSp)
  llRow.appendChild(latLab)
  llRow.appendChild(latVal)
  body.appendChild(llRow)

  const navTargetName =
    addrPlaceholder && addrPlaceholder !== '解析中...'
      ? String(addrPlaceholder).trim()
      : '目的地'
  const navUrl =
    Number.isFinite(Number(lng)) && Number.isFinite(Number(lat))
      ? `https://uri.amap.com/navigation?to=${Number(lng).toFixed(6)},${Number(lat).toFixed(6)},${navTargetName}`
      : ''
  const addrLine = document.createElement('div')
  const addrLab = document.createElement('span')
  addrLab.style.color = labelColor
  addrLab.textContent = '当前位置：'
  const addrSpan = document.createElement('span')
  addrSpan.className = 'bicycle-iw-addr'
  addrSpan.style.cssText = 'color:#1677ff;word-break:break-all'
  addrSpan.textContent = addrPlaceholder
  addrLine.appendChild(addrLab)
  addrLine.appendChild(addrSpan)
  if (navUrl) {
    const navLink = document.createElement('a')
    navLink.href = navUrl
    navLink.target = '_blank'
    navLink.rel = 'noopener noreferrer'
    navLink.textContent = '导航'
    navLink.style.cssText =
      'display:inline-flex;align-items:center;justify-content:center;margin-left:8px;padding:1px 6px;border-radius:4px;background:#1677ff;color:#fff;text-decoration:none;font-size:12px;line-height:1.5;white-space:nowrap'
    navLink.addEventListener('click', (e) => e.stopPropagation())
    addrLine.appendChild(navLink)
  }
  body.appendChild(addrLine)
  addLine('更新时间：', timeStr)
  card.appendChild(body)
  root.appendChild(card)

  const pointer = document.createElement('div')
  pointer.setAttribute('aria-hidden', 'true')
  pointer.style.cssText =
    'width:0;height:0;margin-top:-1px;border-left:9px solid transparent;border-right:9px solid transparent;border-top:10px solid #fff;flex-shrink:0'
  root.appendChild(pointer)

  const infoWindow = new AMap.InfoWindow({
    isCustom: true,
    content: root,
    anchor: 'bottom-center',
    // 车标 PNG 半高 16px，尖角贴在图标上沿
    offset: new AMap.Pixel(0, aboveMarker ? -16 : -8),
    autoMove: false
  })
  infoWindow.open(map, [lng, lat])
  ctx.activeInfoWindow = infoWindow

  if (!row.adr && !row.location) {
    tiandituReverseGeocode(lng, lat).then((r) => {
      const el = root.querySelector('.bicycle-iw-addr')
      if (el && r.success && r.des) el.textContent = r.des
    })
  }
  return infoWindow
}

export function renderLocationMarkers(map, ctx, rows) {
  if (!map || typeof AMap === 'undefined') return []
  const markers = []
  const positions = []
  ;(rows || []).forEach((row, idx) => {
    const [lng, lat] = lonLatToGcj02(row.lon, row.lat)
    positions.push([lng, lat])
    const iconUrl = pickBicycleMarkerIconUrl(row)
    const { root } = buildBicycleMarkerContent(row, iconUrl)
    const marker = new AMap.Marker({
      position: [lng, lat],
      anchor: 'center',
      content: root,
      zIndex: 100 + idx
    })
    marker.on('click', () => {
      openVehicleInfoWindow(map, ctx, lng, lat, row, { aboveMarker: true })
    })
    marker.setMap(map)
    markers.push(marker)
  })
  if (positions.length && typeof map.setFitView === 'function') {
    map.setFitView(markers, true, [40, 40, 40, 40])
  }
  return markers
}
